import {
  addDays,
  addHours,
  addMilliseconds,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  isBefore as dateFnsIsBefore,
  startOfDay as dateFnsStartOfDay,
  startOfMonth as dateFnsStartOfMonth,
  startOfWeek as dateFnsStartOfWeek,
  startOfYear as dateFnsStartOfYear,
  differenceInDays,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  subDays,
  subHours,
  subMilliseconds,
  subMinutes,
  subMonths,
  subSeconds,
  subWeeks,
  subYears,
} from 'date-fns';

import { toDate } from '../formatters';
import type { DateInput } from '../types';

export type DurationUnit = 'millisecond' | 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';

/**
 * Helper function that gets the appropriate date-fns difference function based on the time unit.
 *
 * @param unit - The time unit to get the difference function for.
 * @returns The corresponding date-fns difference function.
 */
const getDifferenceFunction = (unit: DurationUnit): ((dateLeft: Date, dateRight: Date) => number) => {
  switch (unit) {
    case 'second':
      return differenceInSeconds;
    case 'minute':
      return differenceInMinutes;
    case 'hour':
      return differenceInHours;
    case 'day':
      return differenceInDays;
    case 'week':
      return differenceInWeeks;
    case 'month':
      return differenceInMonths;
    case 'year':
      return differenceInYears;
    default:
      return differenceInMilliseconds;
  }
};

/**
 * Helper function that gets the appropriate date-fns add function based on the time unit.
 *
 * @param unit - The time unit to get the add function for.
 * @returns The corresponding date-fns add function.
 */
const getAddFunction = (unit: DurationUnit): ((date: Date, amount: number) => Date) => {
  switch (unit) {
    case 'second':
      return addSeconds;
    case 'minute':
      return addMinutes;
    case 'hour':
      return addHours;
    case 'day':
      return addDays;
    case 'week':
      return addWeeks;
    case 'month':
      return addMonths;
    case 'year':
      return addYears;
    default:
      return addMilliseconds;
  }
};

/**
 * Helper function that gets the appropriate date-fns subtract function based on the time unit.
 *
 * @param unit - The time unit to get the subtract function for.
 * @returns The corresponding date-fns subtract function.
 */
const getSubtractFunction = (unit: DurationUnit): ((date: Date, amount: number) => Date) => {
  switch (unit) {
    case 'second':
      return subSeconds;
    case 'minute':
      return subMinutes;
    case 'hour':
      return subHours;
    case 'day':
      return subDays;
    case 'week':
      return subWeeks;
    case 'month':
      return subMonths;
    case 'year':
      return subYears;
    default:
      return subMilliseconds;
  }
};

/**
 * Calculates the difference between two dates in the specified time unit.
 *
 * @param start - The starting date/time.
 * @param end - The ending date/time.
 * @param unit - The unit of time to measure the difference in (default is 'millisecond').
 * @returns The numeric duration between the two dates in the chosen unit.
 */
export function getDurationBetween(start: DateInput, end: DateInput, unit: DurationUnit = 'millisecond'): number {
  const startDate = toDate(start);
  const endDate = toDate(end);
  const diffFunction = getDifferenceFunction(unit);
  return diffFunction(endDate, startDate);
}

/**
 * Adds a specified duration to a given date.
 *
 * @param date - The base date to add to.
 * @param duration - The amount of time to add.
 * @param unit - The unit corresponding to the duration (e.g., 'day', 'hour').
 * @returns A new Date object with the added duration.
 */
export function addDuration(date: DateInput, duration: number, unit: DurationUnit): Date {
  const baseDate = toDate(date);
  const addFunction = getAddFunction(unit);
  return addFunction(baseDate, duration);
}

/**
 * Subtracts a specified duration from a given date.
 *
 * @param date - The base date to subtract from.
 * @param duration - The amount of time to subtract.
 * @param unit - The unit corresponding to the duration (e.g., 'day', 'hour').
 * @returns A new Date object with the subtracted duration.
 */
export function subtractDuration(date: DateInput, duration: number, unit: DurationUnit): Date {
  const baseDate = toDate(date);
  const subtractFunction = getSubtractFunction(unit);
  return subtractFunction(baseDate, duration);
}

/**
 * Converts a duration value from one time unit to another.
 *
 * @param value - The numeric duration to convert.
 * @param fromUnit - The unit the value is currently in (e.g., 'hour').
 * @param toUnit - The unit to convert the duration to (e.g., 'minute').
 * @returns The equivalent duration expressed in the target unit.
 */
export function convertDuration(value: number, fromUnit: DurationUnit, toUnit: DurationUnit): number {
  const baseDate = new Date(0);
  const addFunction = getAddFunction(fromUnit);
  const targetDate = addFunction(baseDate, value);

  const diffFunction = getDifferenceFunction(toUnit);
  return diffFunction(targetDate, baseDate);
}

/**
 * Checks if the first date is before the second date.
 *
 * @param date1 - The first date to compare.
 * @param date2 - The second date to compare.
 * @returns True if date1 is before date2, false otherwise.
 */
export function isBefore(date1: DateInput, date2: DateInput): boolean {
  return dateFnsIsBefore(toDate(date1), toDate(date2));
}

/**
 * Checks if the first date is at least one day before the second date.
 *
 * @param date1 - The first date to compare.
 * @param date2 - The second date to compare.
 * @returns `true` if date1 is before date2 AND not the same day, `false` otherwise.
 */
export function isBeforeDay(date1: DateInput, date2: DateInput): boolean {
  return !isSameDay(date1, date2) && dateFnsIsBefore(toDate(date1), toDate(date2));
}

/**
 * Gets the start of day for a given date (00:00:00.000).
 *
 * @param date - The date to get the start of day for.
 * @returns A new Date object representing the start of the day.
 */
export function startOfDay(date: DateInput): Date {
  return dateFnsStartOfDay(toDate(date));
}

/**
 * Gets the start of week for a given date (first day of week at 00:00:00.000).
 *
 * @param date - The date to get the start of week for.
 * @returns A new Date object representing the start of the week.
 */
export function startOfWeek(date: DateInput): Date {
  return dateFnsStartOfWeek(toDate(date));
}

/**
 * Gets the start of month for a given date (first day of month at 00:00:00.000).
 *
 * @param date - The date to get the start of month for.
 * @returns A new Date object representing the start of the month.
 */
export function startOfMonth(date: DateInput): Date {
  return dateFnsStartOfMonth(toDate(date));
}

/**
 * Gets the start of year for a given date (first day of year at 00:00:00.000).
 *
 * @param date - The date to get the start of year for.
 * @returns A new Date object representing the start of the year.
 */
export function startOfYear(date: DateInput): Date {
  return dateFnsStartOfYear(toDate(date));
}

/**
 * Checks if two dates are the same day
 * @param date1 - The first date
 * @param date2 - The second date
 * @returns `true` if the dates are the same day; otherwise, `false`.
 */
export function isSameDay(date1: DateInput, date2: DateInput): boolean {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

/**
 * Checks if two dates are the same month
 * @param date1 - The first date
 * @param date2 - The second date
 * @returns `true` if the dates are the same month; otherwise, `false`.
 */
export function isSameMonth(date1: Date, date2: Date): boolean {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
}

/**
 * Checks if two dates are the same year
 * @param date1 - The first date
 * @param date2 - The second date
 * @returns `true` if the dates are the same year; otherwise, `false`.
 */
export function isSameYear(date1: Date, date2: Date): boolean {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  return d1.getFullYear() === d2.getFullYear();
}
